import {
  Alert,
  Badge,
  Button,
  Divider,
  DropzoneReadonly,
  FiFileText,
  FiPlus,
  FiTrash2,
  FormProvider,
  Grid,
  GridItem,
  ReadOnlyField,
  Stack,
  TextField,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { FileMimeType, YES_NO_OPTIONS } from '@bcba/types';
import {
  S3FileUploader,
  validationStatusBadgeColors,
} from '@bcba/frontend-utils';
import { DigitalDossierBankingInformationSerializer } from '@bcba/serializers';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { RadioField } from '../../../RadioField/RadioField';
import { useBankingInfoForm } from './useBankingInfoForm';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import { downloadDocument } from '../../../../utils/downloadDocument.utils';
import { getS3SignedUrl } from '../../../../services/documents/getS3SignedUrl';
import { FormSection } from '../components/FormSection';

type Props = {
  data: DigitalDossierBankingInformationSerializer[];
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<DigitalDossierBankingInformationSerializer[], Error>
  >;
};

export const BankingInfoForm = ({ data, refetch }: Props) => {
  const { t } = useTranslation();

  const {
    formMethods,
    handleSubmit,
    onSubmit,
    hasBankingInfo,
    canUpdate,
    addNewBankAccountHandler,
    removeBankAccountHandler,
    status,
    isPendingChangeRequest,
    validationMessage,
    defaultHasBankingInfo,
    accountsFields,
    actionButtons,
  } = useBankingInfoForm(data, refetch);

  const { register, watch, setValue, resetField } = formMethods;

  const toBeCompletedByMessage = t('common.toBeCompletedByBolsa');

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormSection
          title={t('common.bankData')}
          badge={
            <Badge colorScheme={validationStatusBadgeColors[status]}>
              {t(`validationStatus.${status}`)}
            </Badge>
          }
          action={
            canUpdate && hasBankingInfo ? (
              <Button
                variant={'linkPrimary'}
                onClick={addNewBankAccountHandler}
                leftIcon={<FiPlus />}
              >
                {t('explorer.digitalDossier.updateBankingInfo.addBankAccount')}
              </Button>
            ) : null
          }
        >
          <Stack gap={8}>
            {isPendingChangeRequest && (
              <Alert
                title={t('explorer.changeRequest.alert.title')}
                description={validationMessage}
                variant="error"
              />
            )}
            <RadioField
              name="hasBankingInfo"
              label={t(
                'explorer.digitalDossier.updateBankingInfo.doYouHaveBankingInfo',
              )}
              options={[
                { label: t('common.yes'), value: YES_NO_OPTIONS.YES },
                { label: t('common.no'), value: YES_NO_OPTIONS.NO },
              ]}
              defaultValue={defaultHasBankingInfo}
              rhfOptions={{ disabled: !canUpdate }}
            />
            {hasBankingInfo && (
              <Stack gap={8} divider={<Divider />}>
                {accountsFields.map((field, index) => {
                  const prefix = `accounts.${index}` as const;
                  const existingAccount = data.find(
                    ({ uuid }) => uuid === field.uuid,
                  );
                  const cbuDocFromDb = existingAccount?.cbuDocument;
                  const { identifier, expirationDate } =
                    cbuDocFromDb?.document ?? {};
                  return (
                    <FormSection
                      key={field.id}
                      title={t(
                        'explorer.digitalDossier.updateBankingInfo.accountSection.title',
                      ).replace('{{num}}', String(index + 1))}
                      action={
                        canUpdate && index > 0 ? (
                          <Button
                            variant={'linkDestructive'}
                            leftIcon={<FiTrash2 />}
                            onClick={() => removeBankAccountHandler(index)}
                          >
                            {t('common.delete')}
                          </Button>
                        ) : null
                      }
                    >
                      <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
                        <input
                          style={{ display: 'none' }}
                          {...register(`accounts.${index}.uuid`, {
                            setValueAs: (value: string) => value || undefined,
                          })}
                        ></input>
                        <TextField
                          name={`${prefix}.bankEntity`}
                          label={t(
                            'explorer.digitalDossier.updateBankingInfo.form.bankEntity.label',
                          )}
                          placeholder={t(
                            'explorer.digitalDossier.updateBankingInfo.form.bankEntity.placeholder',
                          )}
                          errorMessageFormatter={t}
                          isReadOnly={!canUpdate}
                          readOnlyValue={existingAccount?.bankEntity || '-'}
                          rhfOptions={{ required: true }}
                        />
                        <TextField
                          name={`${prefix}.cbu`}
                          label={t(
                            'explorer.digitalDossier.updateBankingInfo.form.cbu.label',
                          )}
                          placeholder={t(
                            'explorer.digitalDossier.updateBankingInfo.form.cbu.placeholder',
                          )}
                          errorMessageFormatter={t}
                          isReadOnly={!canUpdate}
                          readOnlyValue={existingAccount?.cbu || '-'}
                          rhfOptions={{ required: true }}
                        />
                        <GridItem colSpan={{ base: 1, md: 2 }}>
                          {cbuDocFromDb &&
                          watch(`${prefix}.cbuDocument.uuid`) ===
                            cbuDocFromDb.uuid ? (
                            <DropzoneReadonly
                              fileName={cbuDocFromDb.document.originalFileName}
                              label={t(
                                'explorer.digitalDossier.updateBankingInfo.form.cbuDocument.label',
                              )}
                              onClick={() =>
                                downloadDocument(cbuDocFromDb.document.uuid)
                              }
                              onDelete={
                                canUpdate
                                  ? () => {
                                      setValue(
                                        `${prefix}.cbuDocument.uuid`,
                                        undefined,
                                        { shouldDirty: true },
                                      );
                                    }
                                  : undefined
                              }
                              leftIcon={FiFileText}
                            />
                          ) : (
                            <S3FileUploader
                              fieldPrefix={`${prefix}.cbuDocument.file.s3TempKey`}
                              label={t(
                                'explorer.digitalDossier.updateBankingInfo.form.cbuDocument.label',
                              )}
                              getS3PreSignedUrl={getS3SignedUrl}
                              onFileUploadSuccess={async (
                                fileName,
                                mimeType,
                                s3TempKey,
                              ) => {
                                setValue(
                                  `${prefix}.cbuDocument.file.name`,
                                  fileName,
                                );
                                setValue(
                                  `${prefix}.cbuDocument.file.mimeType`,
                                  mimeType as FileMimeType,
                                );
                                setValue(
                                  `${prefix}.cbuDocument.file.s3TempKey`,
                                  s3TempKey,
                                );
                                setValue(
                                  `${prefix}.cbuDocument.keepOriginal`,
                                  false,
                                  {
                                    shouldDirty: true,
                                  },
                                );
                              }}
                              onDeleteClick={() => {
                                resetField(
                                  `${prefix}.cbuDocument.file.s3TempKey`,
                                );
                                resetField(`${prefix}.cbuDocument.file.name`);
                                resetField(
                                  `${prefix}.cbuDocument.file.mimeType`,
                                );
                              }}
                              required
                            />
                          )}
                        </GridItem>
                        <ReadOnlyField
                          label={t(
                            'explorer.digitalDossier.updateBankingInfo.form.documentId.label',
                          )}
                          value={identifier || toBeCompletedByMessage}
                        />
                        <ReadOnlyField
                          label={t(
                            'explorer.digitalDossier.updateBankingInfo.form.dueDate.label',
                          )}
                          value={
                            expirationDate
                              ? formatDateToShortFormat(
                                  unixTimestampToDate(expirationDate),
                                )
                              : toBeCompletedByMessage
                          }
                        />
                      </Grid>
                    </FormSection>
                  );
                })}
              </Stack>
            )}
            {actionButtons}
          </Stack>
        </FormSection>
      </form>
    </FormProvider>
  );
};
